import {
  CONSENT_CHANGED_EVENT,
  CONSENT_STORAGE_KEY,
  type ConsentState,
  type CookieCategory,
} from '../types/cookies.types';

const isBrowser = (): boolean => typeof window !== 'undefined';

/** Current policy version; bump to re-prompt users. */
export const DEFAULT_POLICY_VERSION = '1.0';

/**
 * Builds a valid consent state with `necessary` forced to true.
 */
export function buildConsentState(
  partial: Pick<ConsentState, 'analytics' | 'marketing' | 'preferences'>,
  version: string = DEFAULT_POLICY_VERSION,
): ConsentState {
  return {
    necessary: true,
    analytics: partial.analytics,
    marketing: partial.marketing,
    preferences: partial.preferences,
    timestamp: Date.now(),
    version,
  };
}

/**
 * Dispatches a global event when consent changes (for third-party scripts).
 */
export function dispatchConsentChanged(state: ConsentState): void {
  if (!isBrowser()) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<ConsentState>(CONSENT_CHANGED_EVENT, { detail: state }),
  );
}

/**
 * Returns stored consent or null if missing / invalid / outdated version.
 */
export function getConsent(expectedVersion: string = DEFAULT_POLICY_VERSION): ConsentState | null {
  if (!isBrowser()) {
    return null;
  }
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ConsentState;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      parsed.necessary !== true ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.marketing !== 'boolean' ||
      typeof parsed.preferences !== 'boolean' ||
      typeof parsed.timestamp !== 'number' ||
      typeof parsed.version !== 'string'
    ) {
      return null;
    }
    if (parsed.version !== expectedVersion) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persists consent to localStorage and notifies listeners.
 */
export function saveConsent(state: ConsentState): void {
  if (!isBrowser()) {
    return;
  }
  const normalized: ConsentState = {
    ...state,
    necessary: true,
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(normalized));
  dispatchConsentChanged(normalized);
}

/**
 * Whether the user has valid consent for the current policy version.
 */
export function hasConsent(expectedVersion: string = DEFAULT_POLICY_VERSION): boolean {
  return getConsent(expectedVersion) !== null;
}

/**
 * Clears stored consent and notifies listeners with a reject-all state.
 */
export function resetConsent(version: string = DEFAULT_POLICY_VERSION): void {
  if (!isBrowser()) {
    return;
  }
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  dispatchConsentChanged(
    buildConsentState({ analytics: false, marketing: false, preferences: false }, version),
  );
}

/**
 * Checks if a non-necessary category is allowed.
 */
export function isAllowed(
  category: CookieCategory,
  expectedVersion: string = DEFAULT_POLICY_VERSION,
): boolean {
  if (category === 'necessary') {
    return true;
  }
  const consent = getConsent(expectedVersion);
  if (!consent) {
    return false;
  }
  return consent[category];
}
